<template>
    <div class="company-profile-page">
        <breadcrumbs :company="company" />
        <page-title
            v-if="$conf.public.showPageTitles"
            :text="
                company?.name
                    ? localized(company.name, company.language)
                    : $t(`page_titles.company_profile_page_${tabs[selectedTabIndex].name}_tab_title`)
            "
        />
        <div v-else-if="company?.name" class="text-h5 font-weight-bold py-2">
            {{ company.name[company.language] }}
        </div>
        <v-tabs v-model="selectedTabIndex" class="mb-4">
            <v-tab v-for="tab in tabs" :key="tab.name">
                {{ tab.label }}
            </v-tab>
        </v-tabs>
        <v-window v-model="selectedTabIndex" class="company-profile-page__window overflow--visible">
            <v-window-item v-for="tab in tabs" :key="tab.name">
                <company-profile v-if="tab.name === 'info'" :company-data="company" />
                <orders-page
                    v-if="tab.name === 'reservations'"
                    ref="orders"
                    company-page-mode
                    @new-order-request-close="onNewOrderRequestClose"
                >
                    <template v-slot:breadcrumbs><div /> </template>
                    <template v-slot:agentsPaymentsUpload><div /> </template>
                    <template v-slot:bookButtons="{newOrderRequestModal}">
                        <div class="d-flex align-center pb-4">
                            <v-btn class="bg-primary" size="small" @click="searchAndBook">
                                {{ $t('new_order') }}
                            </v-btn>
                            <v-btn
                                v-if="$conf.public.account.newReservationRequest"
                                size="small"
                                class="bg-grey-lighten-4 ms-2"
                                @click="newOrderRequestForClient(newOrderRequestModal)"
                            >
                                {{ $t('new_reservation_request') }}
                            </v-btn>
                        </div>
                    </template>
                </orders-page>
                <template v-if="tab.name === 'users'">
                    <div class="company-profile-page__users-actions d-flex mb-4 align-center">
                        <v-btn
                            :to="{name: 'companyPerson', params: {id: 0, companyId: company.id}}"
                            class="bg-primary me-2"
                        >
                            {{ $t('new_user_profile') }}
                        </v-btn>
                        <file-download-modal
                            v-if="(isTO1 && company.type !== 'CORPORATOR') || isMyCompanyPage"
                            v-model="isShowDownloadExcelMenu"
                            :radio-items="downloadRadioItems"
                            :label="$t('users_download')"
                            :loading="isLoadingDownloadExcel"
                            @change-radio-group="radioGroupDownloadExcel = $event"
                            @download="downloadFile"
                        />
                        <file-upload-modal
                            v-if="isTO1 && company.type !== 'CORPORATOR' && !isMyCompanyPage"
                            v-model="isShowUploadExcelMenu"
                            class="ms-4"
                            :label="$t('users_upload')"
                            :sample-link="sampleLink"
                            @upload="uploadExcel"
                        />
                    </div>
                    <persons-table ref="personsTable" :company="company" :company-id="company.id" :_headers="headers" />
                </template>
                <template v-if="tab.name === 'communications'">
                    <communications
                        :communications="communications"
                        :communications-loading="communicationsLoading"
                        :communications-total-count="communicationsCount"
                        :communication-data-for-create="{
                            accountId: company.id,
                        }"
                        @get-communications="getCommunications"
                    >
                        <template v-slot="{getCommunications}">
                            <communications-filters
                                v-model="communicationFilters"
                                :has-active-filters="hasActiveCommunicationFilters"
                                company-page-mode
                                @submit="getCommunications"
                                @reset="resetCommunicationFilters(getCommunications)"
                            />
                        </template>
                    </communications>
                </template>
                <template v-if="tab.name === 'documents'">
                    <DocumentsTable
                        ref="dataTable"
                        :company-id="company.id"
                        :attach-to-id="company.id"
                        :document-types="useCompanyDocument"
                    />
                </template>
                <template v-if="tab.name === 'settings'">
                    <CompanySettings :company-id="company.id" :is-my-company-page="isMyCompanyPage" />
                </template>
                <template v-if="tab.name === 'departments'">
                    <DepartmentItems :company-id="company.id" />
                </template>
                <template v-if="tab.name === 'offices'">
                    <OfficeItems :company-id="company.id" />
                </template>
                <template v-if="tab.name === 'contract' && company?.id">
                    <CompanyContract
                        :company-id="company.id"
                        :company-type="company.type"
                        is-show-invoices
                        is-show-additionally
                        :contract-group-place="company.type === 'CORPORATOR' ? 'CORPORATE_CLIENT' : 'COMPANY_CONTRACT'"
                    />
                </template>
            </v-window-item>
        </v-window>
        <choose-agent-dialog v-if="isShowSelectAgentDialog" ref="chooseAgentDialog" @save="onChooseAgentSave">
            <template v-slot:activator> <div /> </template>
        </choose-agent-dialog>
    </div>
</template>

<script lang="ts">
    import {Component, Setup, Vue} from 'vue-facing-decorator'
    import CompanyProfile from '~src/components/account/companyProfile.src'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import OrdersPage from '@/pages/account/ordersPage.src'
    import ClientForm from '@/components/account/forms/clientForm.src'
    import {orderFilters} from '@/utils/store-blank-states'
    import PersonsTable from '@/components/account/PersonsTable.vue'
    import ChooseAgentDialog from '~src/components/ChooseAgentDialog.vue'
    import FileUploadModal from '~/components/account/snippets/FileUploadModal.vue'
    import {fullUrl} from '~/utils/host-adapter'
    import FileDownloadModal from '~/components/account/snippets/FileDownloadModal.vue'
    import {downloadData, isEqual} from '~/utils/helpers'
    import CommunicationsPage from '@/pages/CommunicationsPage.vue'
    import Communications from '@/components/account/communications/Communications.vue'
    import CommunicationsFilters from '@/components/account/communications/CommunicationsFilters.vue'
    import {useRecentViewsData} from '~/composables/recent-views'
    import {callComponentMethod} from '~/utils/vue-class-helpers'
    import {initCompanyProfile} from '~/utils/types/account/companies/helpers'
    import DocumentsTable from '~/components/account/DocumentsTable.vue'
    import CompanySettings from '~/components/account/companies/settings/CompanySettings.vue'
    import {Company} from '~/utils/types/types'
    import DepartmentItems from '~/components/account/departments/DepartmentItems.vue'
    import OfficeItems from '~/components/account/offices/OfficeItems.vue'
    import PageTitle from '~/components/parts/PageTitle.vue'
    import {blankCommunicationFilters} from '@/utils/communications-blank-states'

    // TODO [P1] Need chack and fix
    @Component({
        components: {
            OfficeItems,
            DepartmentItems,
            CompanySettings,
            DocumentsTable,
            CommunicationsFilters,
            Communications,
            FileDownloadModal,
            FileUploadModal,
            ChooseAgentDialog,
            PersonsTable,
            ClientForm,
            OrdersPage,
            CompanyProfile,
            Breadcrumbs,
            PageTitle,
        },
        async setup() {
            const app = useNuxtApp()
            const $route = useRoute()
            const params = $route.params
            const {data} = await useAsyncData(`company-profile-${params.id}`, async () => {
                if (
                    params.id === '0' ||
                    (!params.id && $route.name !== 'company-profile' && $route.name !== 'companyPersons')
                ) {
                    return initCompanyProfile()
                }
                const rq = !params.id ? {id: authStore.person.companyId, locale: 'all'} : {id: params.id, locale: 'all'}
                try {
                    const company = (await app.$api.companies.get(rq)).companies[0]
                    if (company) {
                        const lang = 'en'
                        const linkName = company.name[lang]
                        const companyType = company.type
                        await useRecentViewsData($route, linkName, companyType)
                    }
                    return company
                } catch (e) {
                    return initCompanyProfile()
                }
            })
            return {company: data.value}
        },
    })
    export class CompanyProfilePage extends Vue {
        @Setup(() => useLocalized()) localized: Function

        company = initCompanyProfile()
        selectedTabIndex = 0
        isShowSelectAgentDialog = false

        tempFilters = null
        mod = 'company-page-mode'

        isShowDownloadExcelMenu = false
        isLoadingDownloadExcel = false
        radioGroupDownloadExcel = ''

        isShowUploadExcelMenu = false
        isLoadingUploadExcel = false

        communications = []
        communicationsLoading = false
        communicationsCount = 0
        communicationFilters = blankCommunicationFilters()

        onNewOrderRequestClose() {
            persistentStore.SET_SELECTED_COMPANY_AND_AGENT(null)
            persistentStore.SET_SELECTED_PRIVATE_CLIENT(null)
        }

        mounted() {
            if (this.mod === 'company-page-mode') {
                this.tempFilters = persistentStore.orderFilters
                persistentStore.CLEAR_ORDER_FILTERS()
                persistentStore.SET_ORDER_FILTERS(
                    Object.assign({}, persistentStore.orderFilters, {customerCompany: {...this.company}})
                )
            }
            if (this.$route.name === 'companyPersons') {
                this.selectedTabIndex = 1
            }
        }

        beforeUnmount() {
            if (this.mod === 'company-page-mode') {
                persistentStore.SET_ORDER_FILTERS(
                    Object.assign({}, this.tempFilters, {customerCompany: orderFilters()['customerCompany']})
                )
            }
        }

        getCommunications(val) {
            callComponentMethod(this, CommunicationsPage, 'getCommunications', {...val, accountId: this.company.id})
        }

        resetCommunicationFilters(getCommunications) {
            this.communicationFilters = blankCommunicationFilters()
            getCommunications()
        }

        async downloadFile(format) {
            this.isLoadingDownloadExcel = true

            try {
                const data = await this.$api.downloadPersons.get({
                    companyId: this.company.id,
                    fileType: format.fileType,
                })

                await downloadData(data, 'users.xlsx')
                this.isShowDownloadExcelMenu = false
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isLoadingDownloadExcel = false
            }
        }

        async uploadExcel(file) {
            this.isLoadingUploadExcel = true
            const formData = new FormData()
            formData.append('file', file)
            try {
                await this.$api.importUsers.post(formData)
                this.isShowUploadExcelMenu = false
                this.$refs.personsTable?.[0]?.applyFilters()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isLoadingUploadExcel = false
            }
        }

        async searchAndBook() {
            if (this.company.type === 'TOUR_AGENCY' || this.company.type === 'TOUR_OPERATOR') {
                this.isShowSelectAgentDialog = true
                await this.$nextTick()
                this.$refs.chooseAgentDialog.dialog = true
                await this.$nextTick()
                this.$refs.chooseAgentDialog.clientType = 'ta'
                await this.$nextTick()
                this.$refs.chooseAgentDialog.selectedCompany = this.company
                this.$refs.chooseAgentDialog.$refs.tourAgency.$refs.companyAutocomplete.clients = [this.company]
            } else if (this.company.type === 'CORPORATOR') {
                this.setSelectedCompany()
                await this.$router.push({name: 'home'})
            } else {
                await this.$router.push({name: 'home'})
            }
        }

        onChooseAgentSave() {
            this.$router.push({name: 'home'})
        }

        newOrderRequestForClient() {
            this.setSelectedCompany()
            if (this.$refs.orders?.[0]) {
                this.$refs.orders[0].newOrderRequestModal = true
            }
        }

        setSelectedCompany() {
            persistentStore.SET_SELECTED_COMPANY_AND_AGENT({
                company: {
                    ...this.company,
                },
                takeClientMethod: this.isCompanyCorporate ? 'persons' : 'privateClients',
            })
        }

        get tabs() {
            const {type} = this.company
            const isSupplier = type === Company.InternalSupplier || type === Company.HubSupplier

            let tabs = [{name: 'info', label: this.$t('information')}]

            if (this.$route.params.id !== '0') {
                tabs.push({name: 'users', label: this.$t('users')})
                if (this.isMyCompanyPage && this.isTO1) tabs.push({name: 'departments', label: this.$t('departments')})

                if (
                    this.company.type === Company.TourAgency ||
                    this.company.type === Company.TourOperator ||
                    this.company.type === Company.Corporator ||
                    this.company.type === Company.InternalSupplier ||
                    this.company.type === Company.HubSupplier
                ) {
                    tabs.push({name: 'offices', label: this.$t('offices')})
                }
                if (
                    this.company.type === Company.TourAgency ||
                    this.company.type === Company.TourOperator ||
                    this.company.type === Company.Corporator
                ) {
                    tabs.push({name: 'contract', label: this.$t('contract')})
                }
                if (!isSupplier) {
                    tabs.push({name: 'documents', label: this.$t('documents')})
                }

                if (!(authStore.company?.id === this.company?.id)) {
                    tabs.push({name: 'reservations', label: this.$t('orders')})
                }

                if (this.isTO1) tabs.push({name: 'communications', label: this.$t('communications')})

                if (!isSupplier) {
                    tabs.push({name: 'settings', label: this.$t('settings')})
                }
            }

            return tabs
        }

        get isMyCompanyPage(): boolean {
            return authStore.person?.companyId === this.company?.id
        }

        get isTA() {
            return authStore.isAgency
        }

        get isTO2() {
            return authStore.isTO2
        }

        get headers() {
            if (this.isTO1) {
                return [
                    {title: this.$t('department'), value: 'department'},
                    {title: this.$t('phone'), value: 'phone', sortable: true, key: 'PHONE'},
                    {title: this.$t('messenger'), value: 'messenger', sortable: true, key: 'ICQ'},
                ]
            }
            if (this.isTA || this.isTO2) {
                return [{title: this.$t('messenger'), value: 'messenger'}]
            }
            return []
        }

        get hasActiveCommunicationFilters() {
            return !isEqual(blankCommunicationFilters(), this.communicationFilters)
        }

        get isCompanyCorporate() {
            return this.company?.type === 'CORPORATOR'
        }

        get isShowNewOrderRequestModal() {
            return this.$refs.orders?.newOrderRequestModal
        }

        get isTO1() {
            return authStore.isTO1
        }

        get sampleLink() {
            return fullUrl('/stat/files/UsersImport.xlsx')
        }

        get downloadRadioItems() {
            return [{value: 'all', label: this.$t('all_users')}]
        }
    }

    export default CompanyProfilePage
</script>

<style scoped lang="scss">
    :deep(.v-slide-group__next),
    :deep(.v-slide-group__prev) {
        display: none !important;
    }
</style>
